import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import { fromJS, Map } from 'immutable';
import { Link } from 'react-router-dom';
import { Flex, Box } from 'grid-styled';
import PropTypes from 'prop-types';
import connect from '../../store/connect';
import { setViewstate, removeViewstate } from '../../actions/actionCreators';
import { color } from '../../styles/helpers/variables';
import { Container } from '../../styles/Container';
import { NoStyleLink } from '../../styles/NoStyleLink';

class MainMenu extends Component {
  constructor(props) {
    super(props);
    this.hoverTimeout = null;
    this.clearActiveMenuItem = this.clearActiveMenuItem.bind(this);
  }

  setActiveMenuItem(id, itemHasDropdown) {
    if (itemHasDropdown) {
      this.props.removeViewstate('globalSearchActive');
      clearTimeout(this.hoverTimeout);
      this.props.setViewstate('activeMenuItem', id);
    }
  }

  clearActiveMenuItem() {
    clearTimeout(this.hoverTimeout);
    this.hoverTimeout = setTimeout(() => {
      this.props.removeViewstate('activeMenuItem');
    }, 250);
  }

  renderMenuItem() {
    return this.props.sitemap
      .get('children', Map())
      .filter(item => item.get('visibleInMenu'))
      .map(item => {
        const itemHasDropdown = !!item
          .getIn(['children'], Map())
          .filter(itemChild => itemChild.get('visibleInMenu') === true).size;
        const active = this.props.viewstate.get('activeMenuItem') === item.get('id');

        return (
          <MainMenuListItem
            key={item.get('id')}
            onMouseEnter={() => this.setActiveMenuItem(item.get('id'), itemHasDropdown)}
            onMouseLeave={() => this.clearActiveMenuItem()}
          >
            <MainMenuListItemLink to={item.get('url')} active={active}>
              {item.get('title')}
            </MainMenuListItemLink>
            {itemHasDropdown && (
              <Submenu active={active}>
                <Container noPadding>
                  <SubmenuList>
                    <Flex mx="-10px">{this.renderSubMenu(item)}</Flex>
                  </SubmenuList>
                </Container>
              </Submenu>
            )}
          </MainMenuListItem>
        );
      })
      .toArray();
  }

  renderSubMenu(item) {
    // a method with some fancy logic that divides the submenuitems evenly based on how many children they have

    const pageWidth = this.props.viewstate.get('pageWidth');
    let width;

    if (pageWidth < 1100) {
      width = 4;
    } else if (pageWidth < 1300) {
      width = 5;
    } else {
      width = 6;
    }

    const allItems = item.getIn(['children'], Map());

    // get the smallest value of width or the number of children
    const widthOrNumberOfItems = allItems.size < width ? allItems.size : width;

    // define array where result of our logic is placed
    const myArray = [];

    // loop through our children to place the first row of children according to the order set in the CMS
    for (let i = 0; i < widthOrNumberOfItems; i += 1) {
      myArray.push({ counter: allItems.getIn([i, 'children'], Map()).size, items: [i] });
    }

    // loop through the rest of out children and place them in the column with the least number of items
    allItems
      .slice(width)
      .sort((a, b) => {
        if (a.get('children', Map()).size > b.get('children', Map()).size) {
          return -1;
        }
        if (a.get('children', Map()).size < b.get('children', Map()).size) {
          return 1;
        }
        // a must be equal to b
        return 0;
      })
      .forEach((childItem, index) => {
        const adjustedIndex = index + width;
        let smallestIndex = 0;
        myArray.forEach((arrayItem, arrayItemIndex) => {
          if (arrayItem.counter < myArray[smallestIndex].counter + (myArray[smallestIndex].items.length * 2 - 2)) {
            smallestIndex = arrayItemIndex;
          }
        });
        myArray[smallestIndex].counter += childItem.get('children', Map()).size;
        myArray[smallestIndex].items.push(adjustedIndex);
        return true;
      });

    // take the result from our logic and render the DOM according to the order we set
    return myArray.map(block => (
      <Box key={block.items} width={1 / width} px="10px">
        {block.items.map(blockItem => (
          <SubMenuListItem
            key={allItems.get(blockItem).get('url')}
            isCurrentPage={allItems.get(blockItem).get('url') === this.props.location.pathname}
          >
            <NoStyleLink to={allItems.get(blockItem).get('url')}>
              <span>{allItems.get(blockItem).get('title')}</span>
            </NoStyleLink>
            <SubSubMenuList>{this.renderSubMenuItems(allItems.get(blockItem).get('children', Map()))}</SubSubMenuList>
          </SubMenuListItem>
        ))}
      </Box>
    ));
  }

  renderSubMenuItems(itemList) {
    return itemList.map(child => (
      <SubSubMenuListItem key={child.get('url')} isCurrentPage={child.get('url') === this.props.location.pathname}>
        <NoStyleLink to={child.get('url')}>{child.get('title')}</NoStyleLink>
      </SubSubMenuListItem>
    ));
  }

  render() {
    return <MainMenuList>{this.renderMenuItem()}</MainMenuList>;
  }
}

const MainMenuList = styled.ul`
  display: flex;
  align-items: center;
  justify-content: center;
  list-style: none;
  margin: 0;
  padding: 0;
`;

const MainMenuListItem = styled.li`
  min-width: 0;
  margin: 0 20px 0 0;
  &:last-child {
    margin: 0;
  }
`;

const MainMenuListItemLink = styled(Link)`
  display: block;
  padding: 0 0 13px;
  border-bottom: 2px solid transparent;
  color: ${color.white};
  font-size: 16px;
  line-height: 1.25;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  transition: border-bottom-color 0.25s ease-out, color 0.25s ease-out;
  ${props =>
    props.active &&
    css`
      border-bottom-color: ${color.white};
    `};
  &:hover {
    color: rgba(255, 255, 255, 0.875);
  }
  &:active {
    color: rgba(255, 255, 255, 0.75);
  }
`;

const Submenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  padding: 40px 0 80px;
  background-color: ${color.white};
  visibility: hidden;
  z-index: 100;
  ${props =>
    props.active &&
    css`
      visibility: visible;
    `};
`;

const SubmenuList = styled.ul`
  list-style: none;
  padding: 0;
`;

const SubMenuListItem = styled.li`
  font-size: 12px;
  font-weight: 600;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: 1.5px;
  color: ${color.black};
  text-transform: uppercase;
  margin-bottom: 40px;
  &:last-child {
    margin-bottom: 0;
  }

  span {
    display: inline-block;
    margin-bottom: 8px;
  }

  ${props =>
    props.isCurrentPage &&
    css`
      span {
        position: relative;

        &:before {
          content: '';
          position: absolute;
          right: -0.1em;
          bottom: 0;
          left: -0.1em;
          height: 90%;
          opacity: 0.5;
          background-color: #d3d3d3;
          z-index: -1;
        }
      }
    `};
`;

const SubSubMenuList = styled.ul`
  padding: 0;
  list-style: none;
  width: 100%;
`;

const SubSubMenuListItem = styled.li`
  font-size: 14px;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.79;
  letter-spacing: normal;
  color: ${color.black};
  text-transform: none;

  ${props =>
    props.isCurrentPage &&
    css`
      a {
        position: relative;

        &:before {
          content: '';
          position: absolute;
          right: -0.1em;
          bottom: 0;
          left: -0.1em;
          height: 90%;
          opacity: 0.5;
          background-color: #d3d3d3;
          z-index: -1;
        }
      }
    `};
`;

MainMenu.defaultProps = {
  viewstate: Map(),
  sitemap: Map()
};

MainMenu.propTypes = {
  setViewstate: PropTypes.func.isRequired,
  removeViewstate: PropTypes.func.isRequired,
  viewstate: PropTypes.instanceOf(Map),
  location: PropTypes.object.isRequired,
  sitemap: PropTypes.instanceOf(Map)
};

export default connect(MainMenu, { setViewstate, removeViewstate }, store => ({
  viewstate: store.viewstate,
  sitemap: fromJS(window.sitemap)
}));



// WEBPACK FOOTER //
// ./src/components/Header/MainMenu.js